/* ═══════════════════════════════════════════════
   CENTRAL STRATEGY DATA — shared across all pages
   Category criteria align with ISINCategoryServiceImpl
   ═══════════════════════════════════════════════ */

/** Resolve active sub-category criteria for a strategy universe page. */
export function getStrategyCategoryInfo(strategyId, categories) {
    const strategy = STRATEGIES.find((s) => s.id === strategyId);
    if (!strategy?.categoryBreakdown?.length) return null;

    const cats = Array.isArray(categories) ? categories.filter(Boolean) : [];
    if (cats.length === 0) {
        return strategy.categoryBreakdown.find((c) => c.categoryKey === 'all') || null;
    }

    const key = String(cats[0]).trim().toLowerCase();
    return strategy.categoryBreakdown.find((c) => c.categoryKey === key) || null;
}

/** User-selectable categories for a strategy (excludes the unrestricted "all" bucket). */
export function getStrategySelectableCategories(strategyId) {
    const strategy = STRATEGIES.find((s) => s.id === strategyId);
    if (!strategy?.categoryBreakdown?.length) return [];
    return strategy.categoryBreakdown.filter((c) => c.categoryKey !== 'all');
}

/** Default category key when a strategy exposes selectable categories. */
export function getStrategyDefaultCategoryKey(strategyId) {
    return getStrategySelectableCategories(strategyId)[0]?.categoryKey ?? null;
}

export const STRATEGIES = [
    {
        id: 'principal-protection',
        route: '/construct-portfolio/principal-protection',
        icon: '🛡️',
        title: 'Principal Protection',
        tagline: 'Capital Safety First',
        description:
            'Sovereign-backed instruments for investors who prioritise capital safety over yield.',
        categoryBreakdown: [
            {
                categoryKey: 'government',
                name: 'Central Government Securities (G-Sec)',
                criteria: 'ISINs sourced from RBI central government securities — direct obligations of the Government of India with sovereign guarantee.',
            },
        ],
        heroDescription:
            'This strategy is built entirely on Sovereign-backed instruments — Government Securities (G-Sec) and Treasury Bills — guaranteed by the Government of India. It is the safest category of fixed-income investments, designed for investors whose top priority is getting their principal back intact.',
        risk: 'Low',
        riskClass: 'low',
        instruments: ['G-Sec', 'T-Bills', 'SDL'],
        gradientFrom: '#0f172a',
        gradientTo: '#1e3a5f',
        accentColor: '#38bdf8',
        features: [
            'Zero default risk via Government backing',
            'Ideal for capital preservation goals',
            'Suitable for conservative/short-term investors',
            'Highly liquid secondary market',
        ],
    },
    {
        id: 'regular-income',
        route: '/construct-portfolio/regular-income',
        icon: '💵',
        title: 'Regular Income',
        tagline: 'Stable Cash Flow, Every Quarter',
        description:
            'Top-rated corporate bonds selected for steady coupon income and manageable credit risk.',
        categoryBreakdown: [
            {
                categoryKey: 'investmentgradebonds',
                name: 'Investment Grade Bonds',
                criteria: 'AA+ to AAA rated; listed, fixed-coupon, senior bonds — secured (or bank-issued) and without call/put options.',
            },
            {
                categoryKey: 'regularincomebonds',
                name: 'Regular Income Bonds',
                criteria: 'AA− to AAA rated; listed, fixed-coupon, senior bonds without call/put options — broader AA band for income portfolios.',
            },
        ],
        heroDescription:
            'This strategy targets Investment Grade bonds (AAA/AA rated) from PSUs, NBFCs, and top-rated corporates that offer regular coupon payouts. It balances yield pick-up over G-Secs while maintaining very low credit risk, making it ideal for retirees and income-focused investors.',
        risk: 'Low-Medium',
        riskClass: 'low-medium',
        instruments: ['AAA Bonds', 'PSU Bonds', 'NCD'],
        gradientFrom: '#0f2c1a',
        gradientTo: '#14532d',
        accentColor: '#4ade80',
        features: [
            'Quarterly / semi-annual coupon payouts',
            'AAA/AA rated issuer universe',
            'Higher yield than G-Secs with manageable risk',
            'Good secondary market liquidity',
        ],
    },
    {
        id: 'target-return',
        route: '/construct-portfolio/target-return',
        icon: '🎯',
        title: 'Target Return Portfolio',
        tagline: 'Goal-Oriented Bond Allocation',
        description:
            'Filter across the full bond universe to assemble holdings that meet a defined return objective.',
        categoryBreakdown: [
            {
                categoryKey: 'all',
                name: 'All Instruments',
                criteria: 'No category restriction — browse sovereign, corporate, and specialty bonds and apply issuer, rating, coupon, and maturity filters to build toward your target yield.',
            },
        ],
        heroDescription:
            'This strategy is designed around a user-defined return target. The portfolio blends AA+ and AA rated corporate bonds with select G-Secs to construct a blended yield that achieves the target. It is suited for goal-based investors — funding children\'s education, a property purchase, or retirement corpus.',
        risk: 'Medium',
        riskClass: 'medium',
        instruments: ['AA Bonds', 'A+ Bonds', 'G-Sec'],
        gradientFrom: '#1c1503',
        gradientTo: '#431407',
        accentColor: '#fbbf24',
        features: [
            'Portfolio built around your return target',
            'Mix of G-Sec and corporate bonds',
            'AA/AA+ rated issuer universe',
            'Defined maturity horizon alignment',
        ],
    },
    {
        id: 'high-return',
        route: '/construct-portfolio/high-return',
        icon: '📈',
        title: 'High Return',
        tagline: 'Maximise Yield, Accept Higher Risk',
        description:
            'Higher-yielding corporate bonds for investors willing to accept greater credit risk.',
        categoryBreakdown: [
            {
                categoryKey: 'highyieldbonds',
                name: 'High Yield Bonds',
                criteria: 'BBB to A− rated; fixed-coupon, senior bonds without call/put options — yield pick-up over investment-grade issuers.',
            },
            {
                categoryKey: 'subinvestmentgradebonds',
                name: 'Sub-Investment Grade Bonds',
                criteria: 'Below BBB or unrated; senior bonds without call/put options — highest yield but elevated default risk.',
            },
        ],
        heroDescription:
            'This strategy targets Sub-Investment Grade bonds (A and below) and high-yield NCDs from growing companies. The trade-off is higher credit risk in exchange for significantly better returns compared to the market. Suitable for sophisticated investors with a higher risk appetite and longer horizons.',
        risk: 'High',
        riskClass: 'high',
        instruments: ['BB Bonds', 'HY NCDs', 'A Rated'],
        gradientFrom: '#1a0505',
        gradientTo: '#450a0a',
        accentColor: '#f87171',
        features: [
            'Significantly higher coupon vs benchmarks',
            'Sub-Investment Grade issuer universe',
            'Higher concentration risk — requires diversification',
            'Suitable for 3–5+ year horizon',
        ],
    },
    {
        id: 'liquidity',
        route: '/construct-portfolio/liquidity',
        icon: '💧',
        title: 'Liquidity',
        tagline: 'Near-Cash Flexibility with Returns',
        description:
            'Most liquid, highest-quality corporate bonds suitable for near-cash portfolio needs.',
        categoryBreakdown: [
            {
                categoryKey: 'highqualityliquidbonds',
                name: 'High Quality Liquid Bonds',
                criteria: 'Dual AAA rated; large market-cap issuers with high aggregate bond borrowings; listed, fixed-coupon, bullet-redemption, senior secured bonds with active secondary-market liquidity.',
            },
        ],
        heroDescription:
            'This strategy invests in ultra-short duration, high-quality instruments — T-Bills, State Development Loans (SDLs), and Certificates of Deposit. Designed for liquidity-first investors who need their money to remain accessible while still earning better than savings rate returns.',
        risk: 'Low',
        riskClass: 'low',
        instruments: ['T-Bills', 'SDL', 'CDs', 'CP'],
        gradientFrom: '#031220',
        gradientTo: '#0c4a6e',
        accentColor: '#67e8f9',
        features: [
            'Short-duration instruments (< 1 year)',
            'Highest liquidity in fixed income',
            'Sovereign/AAA instrument universe',
            'Ideal for emergency / operational corpus',
        ],
    },
    {
        id: 'modified-risk',
        route: '/construct-portfolio/modified-risk',
        icon: '⚖️',
        title: 'Modified Risk',
        tagline: 'Balanced Diversification Across Asset Classes',
        description:
            'Non-vanilla fixed income spanning structured products, hybrids, infrastructure trusts, and sub-sovereign debt.',
        categoryBreakdown: [
            {
                categoryKey: 'structuredbonds',
                name: 'Structured Bonds',
                criteria: 'BBB to AAA rated; guaranteed bonds or non-fixed-coupon structures; senior issues classified outside standard vanilla NCDs.',
            },
            {
                categoryKey: 'hybrids',
                name: 'Hybrids',
                criteria: 'Bonds with embedded call/put options, or subordinated / perpetual structures that carry equity-like features.',
            },
            {
                categoryKey: 'invit',
                name: 'InvIT',
                criteria: 'ISINs issued by infrastructure investment trusts — issuers classified under InvIT basic industry in master issuer data.',
            },
            {
                categoryKey: 'reit',
                name: 'REIT',
                criteria: 'ISINs issued by real estate investment trusts — issuers classified under REIT basic industry in master issuer data.',
            },
            {
                categoryKey: 'stategovernment',
                name: 'State Government (SDL)',
                criteria: 'State Development Loans from RBI state government securities — direct obligations of Indian state governments.',
            },
        ],
        heroDescription:
            'This strategy creates a diversified blend across REITs, InvITs, Hybrid Bonds, Structured Notes, and Sub-Sovereign Securities. It is designed to reduce concentration in any single asset class while capturing higher risk-adjusted returns than plain vanilla bond portfolios.',
        risk: 'Medium',
        riskClass: 'medium',
        instruments: ['REIT', 'InvIT', 'Hybrid', 'Structured'],
        gradientFrom: '#130f2a',
        gradientTo: '#2e1065',
        accentColor: '#a78bfa',
        features: [
            'Cross-asset class diversification',
            'REIT, InvIT, Hybrid, and Structured bonds',
            'Better risk-adjusted returns than plain bonds',
            'Suitable for moderate risk appetite',
        ],
    },
];
